import React, { useState } from 'react'
import { Employee } from '@/types/workforce'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { RecipientCard } from './RecipientCard'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'
import { useAuth } from '@/hooks/useAuth'
import { getEmployee } from '@/api/workforce'
import { Users, X, Search } from 'lucide-react'

interface RecipientSelectorProps {
  directReports: Employee[]
  loading: boolean
  selectedRecipient: Employee | null
  onSelectRecipient: (employee: Employee | null) => void
}

export function RecipientSelector({
  directReports,
  loading,
  selectedRecipient,
  onSelectRecipient
}: RecipientSelectorProps) {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [loadingEmployee, setLoadingEmployee] = useState(false)

  // Determine if user should use search mode (RH, analista-rh, vice-presidente)
  const shouldUseSearchMode = user?.role === 'rh' || user?.role === 'analista-rh' || user?.role === 'vice-presidente'

  const filteredReports = directReports.filter(employee => {
    const name = employee.personalInfo?.name?.toLowerCase() || ''
    const email = employee.email?.toLowerCase() || ''
    const role = employee.currentEngagement?.roleName?.toLowerCase() || ''
    const query = searchQuery.toLowerCase()

    return name.includes(query) || email.includes(query) || role.includes(query)
  })

  const handleAutocompleteSelect = async (employeeId: string) => {
    try {
      setLoadingEmployee(true)
      const tenantDomain = user?.employee?.tenantId || 'monumenta'
      const employeeData = await getEmployee(tenantDomain, employeeId)
      onSelectRecipient(employeeData)
    } catch (err) {
      console.error('Error fetching employee:', err)
    } finally {
      setLoadingEmployee(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-primary" />
            Destinatário <span className="text-error">*</span>
          </CardTitle>
          <CardDescription>
            Carregando lista de subordinados diretos...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="text-brand-gray font-body mt-4">
                Carregando subordinados diretos...
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          {shouldUseSearchMode ? (
            <Search className="h-5 w-5 text-brand-primary" />
          ) : (
            <Users className="h-5 w-5 text-brand-primary" />
          )}
          Destinatário <span className="text-error">*</span>
        </CardTitle>
        <CardDescription>
          {shouldUseSearchMode
            ? 'Busque qualquer colaborador da empresa'
            : 'Selecione um subordinado direto da sua equipe'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selected Recipient Display */}
        {selectedRecipient && (
          <div className="bg-brand-primary/10 border border-brand-primary/30 rounded-monumenta p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brand-gray font-body mb-1">Destinatário selecionado:</p>
                <p className="text-white font-semibold font-body">
                  {selectedRecipient.personalInfo?.name}
                </p>
                <p className="text-sm text-brand-gray font-body">
                  {selectedRecipient.currentEngagement?.roleName} • {selectedRecipient.currentEngagement?.departmentName}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelectRecipient(null)}
                className="btn-ghost text-brand-gray hover:text-white hover:bg-brand-dark"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Search Mode: EmployeeAutocomplete for RH/VP */}
        {shouldUseSearchMode ? (
          <div className="space-y-4">
            <EmployeeAutocomplete
              onSelect={handleAutocompleteSelect}
              navigateOnSelect={false}
              placeholder="Buscar por nome ou email..."
            />
            {loadingEmployee && (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="sm" />
                <span className="ml-2 text-brand-gray text-sm">Carregando...</span>
              </div>
            )}
          </div>
        ) : directReports.length > 0 ? (
          <>
            {/* Search Bar for direct reports */}
            {directReports.length > 4 && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Buscar por nome, email ou cargo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {/* Recipients Grid */}
            <div>
              <p className="text-sm text-brand-gray font-body mb-3">
                Selecione um dos seus subordinados diretos:
              </p>
              {filteredReports.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {filteredReports.map((employee) => (
                    <RecipientCard
                      key={employee.employeeId}
                      employee={employee}
                      isSelected={selectedRecipient?.employeeId === employee.employeeId}
                      onSelect={onSelectRecipient}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-brand-gray">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="font-body">Nenhum subordinado encontrado com "{searchQuery}"</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 mx-auto mb-3 text-brand-gray" />
            <p className="text-brand-gray font-body">
              Você não possui subordinados diretos no momento.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
